import type { CrestThemeName } from '@crest/web-common/theme/antd-theme.js';
import { loadRemote, registerRemotes } from '@module-federation/enhanced/runtime';
import type { RuntimeMicroApp } from './app-runtime-context.js';

export type FederatedAppName = 'crm' | 'operations' | 'agent';

export interface FederatedNavigationBridge {
  pathname: string;
  search: string;
  hash: string;
  initialPathname: string;
  navigate: (path: string) => void;
  /** 获取只面向当前 Remote API 的短期用户令牌；Remote 不得持久化。 */
  getAccessToken: (options?: { force?: boolean }) => Promise<string>;
}

export interface FederatedLifecycleProps {
  container: HTMLElement;
  releaseId: string;
  platform: FederatedNavigationBridge;
  /** 当前用户界面主题；Remote 未识别时可忽略并回退自身默认主题。 */
  theme?: CrestThemeName;
  assistant?: { open: boolean; onOpenChange: (open: boolean) => void };
}

export interface FederatedLifecycleModule {
  capabilities?: { assistantWindow?: boolean };
  mount: (props: FederatedLifecycleProps) => void | Promise<void>;
  update?: (props: FederatedLifecycleProps) => void | Promise<void>;
  unmount: (target?: { container: HTMLElement }) => void | Promise<void>;
}

export interface FederatedRemoteRelease {
  appName: FederatedAppName;
  remoteName: 'crest_crm' | 'crest_operations' | 'crest_agent';
  manifestUrl: string;
  exposedModule: './lifecycle';
  releaseId: string;
  commitSha: string | null;
  enabled: boolean;
}

const expectedRemoteNames: Record<FederatedAppName, FederatedRemoteRelease['remoteName']> = {
  crm: 'crest_crm',
  operations: 'crest_operations',
  agent: 'crest_agent',
};
const appDisplayNames: Record<FederatedAppName, string> = {
  crm: 'CRM',
  operations: 'Operations',
  agent: 'Agent',
};

const registeredEntries = new Map<string, string>();
const failedEntries = new Set<string>();

export async function loadFederatedApp(
  appName: FederatedAppName,
  platformRelease: RuntimeMicroApp,
): Promise<{
  module: FederatedLifecycleModule;
  remote: FederatedRemoteRelease;
}> {
  const remote = parsePlatformRelease(appName, platformRelease);
  await verifyRemoteManifestMetadata(remote, platformRelease.commitSha);

  const previousEntry = registeredEntries.get(remote.remoteName);
  const retryFailedEntry = failedEntries.has(remote.remoteName);
  if (previousEntry && previousEntry !== remote.manifestUrl && !retryFailedEntry) {
    throw new Error(appName + ' 的 Remote 版本已变化，请刷新 Portal Shell 后重试');
  }
  if (!previousEntry || retryFailedEntry) {
    const registration = [{ name: remote.remoteName, entry: remote.manifestUrl }];
    if (retryFailedEntry) registerRemotes(registration, { force: true });
    else registerRemotes(registration);
    registeredEntries.set(remote.remoteName, remote.manifestUrl);
  }

  const moduleId = remote.remoteName + '/' + remote.exposedModule.replace(/^\.\//, '');
  let loaded: FederatedLifecycleModule | null;
  try {
    loaded = await loadRemote<FederatedLifecycleModule>(moduleId);
    failedEntries.delete(remote.remoteName);
  } catch (error) {
    failedEntries.add(remote.remoteName);
    throw error;
  }
  if (!loaded || typeof loaded.mount !== 'function' || typeof loaded.unmount !== 'function') {
    throw new Error(appName + ' Remote 未实现 mount/unmount 生命周期契约');
  }
  return { module: loaded, remote };
}

async function verifyRemoteManifestMetadata(
  remote: FederatedRemoteRelease,
  expectedCommitSha: string,
): Promise<void> {
  const response = await fetch(remote.manifestUrl, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`${remote.appName} MF Manifest 校验失败（HTTP ${response.status}）`);
  }
  const manifest: unknown = await response.json();
  if (!isRecord(manifest)) throw new Error(`${remote.appName} MF Manifest 格式无效`);
  if (manifest.releaseId !== remote.releaseId) {
    throw new Error(`${remote.appName} Registry 与 MF Manifest releaseId 不一致`);
  }
  if (
    typeof manifest.commitSha !== 'string' ||
    manifest.commitSha.toLowerCase() !== expectedCommitSha.toLowerCase()
  ) {
    throw new Error(`${remote.appName} Registry 与 MF Manifest commitSha 不一致`);
  }
}

export function resetMfRuntimeForTests() {
  registeredEntries.clear();
  failedEntries.clear();
}

function parsePlatformRelease(
  appName: FederatedAppName,
  release: RuntimeMicroApp,
): FederatedRemoteRelease {
  const displayName = appDisplayNames[appName];
  const expectedMicroAppName =
    appName === 'crm' ? 'crest-crm' : appName === 'operations' ? 'crest-operations' : 'crest-agent';
  if (release.appName !== appName || release.microAppName !== expectedMicroAppName) {
    throw new Error(displayName + ' Release 身份不匹配');
  }
  if (!isNonEmptyString(release.releaseId) || !/^[0-9a-f]{40}$/i.test(release.commitSha)) {
    throw new Error(displayName + ' Release 版本信息无效');
  }
  return {
    appName,
    remoteName: expectedRemoteNames[appName],
    manifestUrl: resolveManifestUrl(release.entry, window.location.href),
    exposedModule: './lifecycle',
    releaseId: release.releaseId,
    commitSha: release.commitSha,
    enabled: true,
  };
}

function resolveManifestUrl(value: string, baseUrl: string): string {
  const url = new URL(value, baseUrl);
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) {
    throw new Error('Remote manifest 只允许无凭据的 HTTP(S) URL');
  }
  if (window.location.protocol === 'https:' && url.protocol !== 'https:') {
    throw new Error('HTTPS Portal Shell 禁止加载非 HTTPS Remote');
  }
  return url.toString();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
